import { useEffect, useState } from "react";
import Flex from "../Flex/Flex";
import { BuiltQuery, BuiltQueryKeepa, ParseError, ParseErrorType, parseQuery, queryToKeepa, safeEntries, TokenType } from "@boxem/core-query-language";
import { Tooltip } from "../Tooltip/Tooltip";
import Button from "../forms/Button/Button";
import Autocomplete from "../forms/Autocomplete/Autocomplete";
import InputNumber from "../forms/InputNumber/InputNumber";
import IconInfo from "../icons/IconInfo";
import { getSearchableParamByName, isNumericType, SearchableParamType } from "@boxem/core-query-language/lib/searchable-params";
import Divider from "../Divider/Divider";
import Checkbox from "../forms/Checkbox/Checkbox";
import IconDelete from "../icons/iconDelete";
import IconPlus from "../icons/IconPlus";
import TextInput from "../forms/TextInput/TextInput";
import { logRocketError } from "@/utils/logUtils";
import { useSearch } from "wouter";
import CopyToClipBoard from "../CopyToClipboard/CopyToClipboard";
import Alert from "../Alert/Alert";
import { getLocalStorageItem, LocalStorageKeys } from "@/utils/localStorageUtils";
import { productFinderCacheCheck } from "@/api/product-research";
import IconSearch from "../icons/IconSearch";
import Select from "../forms/Select/Select";
import { IconBoxemB } from "../icons/IconBoxemB";
import IconCopyClipboard from "../icons/IconCopyClipboard";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../Modal/Modal";

import styles from "./QueryAwareTextInput.module.css";
import Textarea from "../forms/Textarea/Textarea";


const cacheCheckDebounce = { id: 0 };
export interface QueryAwareTextInputProps {
  loading?: boolean;

  query: string;
  setQuery: (v: string) => void;

  onChange?: (builtQuery: BuiltQuery[], builtQueryKeepa: BuiltQueryKeepa, parseError: ParseError) => void;
  onSearch?: () => void;
  clearSearch?: () => void;
}

interface SavedSearchModalProps {
  name: string;
  description: string;
}

const queryTextInputId = "query-text-input-id";
export function QueryAwareTextInput(props: QueryAwareTextInputProps) {
  const { loading, query, setQuery, onChange, onSearch, clearSearch } = props;

  const urlParams = new URLSearchParams(useSearch());

  const [saveSearchModal, setSaveSearchModal] = useState<SavedSearchModalProps>(null);

  const [allowSave, setAllowSave] = useState<boolean>(false);
  const [manualParams, setManualParams] = useState<ParamRow[]>([new ParamRow()]);
  const [queryResult, setQueryResult] = useState<{ builtQuery?: BuiltQuery[], builtQueryKeepa?: BuiltQueryKeepa, parseError?: ParseError }>(null);
  console.log(JSON.stringify(queryResult));

  const showDropdown = !!queryResult?.parseError && queryResult?.parseError?.type !== ParseErrorType.emptyExpression && query?.trim()?.length > 0;

  const [selectedSavedSearch, setSelectedSavedSearch] = useState<SavedSearch>(null);
  const _savedSearches = getLocalStorageItem(LocalStorageKeys.productResearchProductFinderSavedSearches, defaultSavedSearches);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(_savedSearches);

  const builtQueryToParamRows = (builtQuery: BuiltQuery[]) => {
    const params: ParamRow[] = [];
    for (const query of builtQuery) {
      for (const [key, value] of safeEntries(query)) {
        if (key === "title") continue;

        const displayName = searchableParamNamesToNiceName.get(key);
        if (!displayName) continue;
        let param = params.find(p => p.name === displayName);
        if (!param) {
          param = new ParamRow();
          params.push(param);
        }
        param.name = displayName;
        switch (value.operationNode.token.type) {
          case TokenType.minus:
            break;

          case TokenType.equals:
            if (isNumericType(value.type)) {
              param.lte = (value.value as number);
              param.gte = (value.value as number);
            } else if (value.type === SearchableParamType.string) {
              param.str = value.value as string;
            } else if (value.type === SearchableParamType.boolean) {
              param.checked = value.value as boolean;
            }
            break;

          case TokenType.notEquals:
            param.lte = (value.value as number) + 1;
            param.gte = (value.value as number) - 1;
            break;

          case TokenType.greaterThan:
            param.gte = (value.value as number) - 1;
            break;
          case TokenType.greaterThanOrEquals:
            param.gte = (value.value as number);
            break;
          case TokenType.lessThan:
            param.lte = (value.value as number) + 1;
            break;
          case TokenType.lessThanOrEquals:
            param.lte = (value.value as number);
            break;

          case TokenType.not:
            break;
        }
      }
    }
    setManualParams(params);
  };

  const setQueryFromParamRows = (paramRows: ParamRow[]) => {
    // populate the advanced query based on the changes done to the manual search:
    let isFirst = true;
    let newQuery = "";
    for (const param of paramRows) {
      if (!param.name) continue;
      const keepaName = searchableParams.get(param.name);
      const sp = getSearchableParamByName(keepaName);
      if (!sp) {
        logRocketError(`Failed to find searchable param for name: ${param.name} and ${keepaName}`);
        continue;
      }

      const handleType = (type: SearchableParamType) => {
        switch (type) {
          case SearchableParamType.array:
            handleType(sp.arrayType);
            break;

          case SearchableParamType.boolean:
            newQuery += `${!isFirst ? " and" : ""} ${param.checked ? "" : "not "}${keepaName}`;
            isFirst = false;
            break;

          case SearchableParamType.byte:
          case SearchableParamType.integer:
          case SearchableParamType.long:
            if (param.gte !== 1) { // '1' is the default, so we don't populate it.
              newQuery += `${!isFirst ? " and" : ""} ${keepaName} >= ${param.gte}`;
              isFirst = false;
            }
            if (param.lte) {
              newQuery +=`${!isFirst ? " and" : ""} ${keepaName} <= ${param.lte}`;
              isFirst = false;
            }
            break;

          case SearchableParamType.string:
            newQuery +=`${!isFirst ? " and" : ""} ${keepaName} = '${param.str}'`;
            isFirst = false;
            break;

          // @TODO:
          case SearchableParamType.date:
            break;
        }
        isFirst = false;
      }

      handleType(sp.type);
    }

    // update the query itself from the manual params:
    setQuery(newQuery)
  };

  useEffect(() => {

    clearTimeout(cacheCheckDebounce.id);
    if (query) {
      // check if we're cached and therefore can automatically show search results:
      cacheCheckDebounce.id = window.setTimeout(() => {
        productFinderCacheCheck(query)
        .then(result => {
          if (result?.isCached) {
            onSearch?.();
          }
        });
      }, 350);

      // update param rows (manual search) from the advanced search query
      const setParamRowsFromQuery = () => {
        if (query) {
          const result = parseQuery(query);
          if (result.parseError) {
            setQueryResult({ builtQuery: result.builtQuery, builtQueryKeepa: null, parseError: result.parseError });
            onChange?.(result.builtQuery, null, result.parseError);
            return;
          }
          const keepa = queryToKeepa(result.builtQuery);
          setQueryResult({ builtQuery: result.builtQuery, builtQueryKeepa: keepa.builtQuery, parseError: keepa.parseError });
          onChange?.(result.builtQuery, keepa.builtQuery, keepa.parseError);
      
          builtQueryToParamRows(result.builtQuery);
        }
      };

      setParamRowsFromQuery();

      // if we set the query to something other than what the current saved search is, show option to allow saving search:
      let _allowSave = false;
      if (!!selectedSavedSearch) {
        if (selectedSavedSearch.query !== query) {
          _allowSave = true;
        }
      } else {
        _allowSave = true;
      }
      setAllowSave(_allowSave);
    }
  }, [query]);

  // useEffect(() => {
  //   // update query from browser url:
  //   const urlQuery = urlParams.get("q");
  //   if (urlQuery && (!query || query.trim() === "")) {
  //     try {
  //       const decoded = decodeURIComponent(urlQuery);
  //       setQuery(decoded);
  //       const result = parseQueryKeepa(decoded);
  //       setQueryResult(result);
  //       onChange?.(decoded, result.builtQuery, result.parseError);
  //     } catch (e) {}
  //   }
  // }, [urlParams]);

  // useEffect(() => {
  //   // set url query from text query:
  //   if (query) {
  //     urlParams.set("q", encodeURIComponent(query));
  //     const newUrl = "?" + urlParams.toString();
  //     window.history.replaceState(null, "", newUrl);
  //   }
  // }, [query]);

  return (
    <Flex direction="column" gap={4}>

      <Modal
        className={styles.saveSearchModal}
        open={!!saveSearchModal}
        onOpenChange={open => {
          if (!open) setSaveSearchModal(null);
        }}
      >
        {!!saveSearchModal && (
          <>
            <ModalHeader>Save New Search</ModalHeader>
            <ModalBody> 
              <Flex direction="column" gap={4}>
                <label className={styles.inputLabelRow}>
                  <span className="weak">Name:</span>
                  <TextInput
                    inputAutofocus
                    minLength={1}
                    maxLength={100}
                    value={saveSearchModal.name}
                    onChange={v => {
                      setSaveSearchModal({ ...saveSearchModal, name: v });
                    }}
                    placeholder="enter a name for your saved search"
                  />
                </label>

                <Flex direction="column" gap={1}>
                  <label className="weak" htmlFor="savedSearchDescription">Description:</label>
                  <Textarea
                    inputId="savedSearchDescription"
                    value={saveSearchModal.description}
                    maxLength={255}
                    onChange={v => {
                      setSaveSearchModal({ ...saveSearchModal, description: v });
                    }}
                  />
                </Flex>
              </Flex>
             
            </ModalBody>
            <ModalFooter>
              <Flex justify="end">
                <Button
                  disabled={!saveSearchModal || saveSearchModal.name.length === 0 || saveSearchModal.name.length > 100}
                  onClick={() => {
                    clearSearch();
                    if (selectedSavedSearch) {
                      savedSearches.unshift({ ...selectedSavedSearch, name: saveSearchModal.name, description: saveSearchModal.description, isBoxemPreset: false });
                    } else {
                      savedSearches.unshift({ query, name: saveSearchModal.name, description: saveSearchModal.description, isBoxemPreset: false }); 
                    }
                    setSavedSearches([...savedSearches]);
                    setSelectedSavedSearch(savedSearches[0]);
                    setAllowSave(false);
                    setSaveSearchModal(null);
                  }}
                >
                  Save as New Search
                </Button>
              </Flex>
            </ModalFooter>
          </>
        )}
      </Modal>

      <Alert
        type="info"
        message={"Boxem product finder is a tool to help you do product research for Amazon FBA/FBM. It lets you make advanced searches by key product attributes like listing price, sales rank, live offer count, and much more. Note that data can fluctuate rapidly, and so results are never garunteed to be accurate to the minute."}
      />

      <Flex gap={2} align="center">
        <h3 className={styles.noShrink}>Saved Searches:</h3>
        <div className={styles.savedSearchSelect}>
          <Select
            fullWidth
            placeholder="select a saved search or preset"
            value={selectedSavedSearch}
            options={savedSearches}
            optionRender={(o, i) => {
              return (
                <Flex direction="column" gap={2} className={styles.savedSearch}>
                  <div className={styles.savedSearchTopRow}>
                    <Flex gap={2} align="center">
                      {o.isBoxemPreset && 
                        <Tooltip
                          title={"This is a Boxem Preset"}
                          placement="bottom-right"
                        >
                          <IconBoxemB/>
                        </Tooltip>
                      }
                      <h6 className={`strong ${styles.noShrink}`}>{o.name}</h6>
                    </Flex>

                    <code className={styles.savedSearchCode}>
                      {o.query}
                    </code>
                  </div>
                  <p className={`small-text ${styles.savedSearchDescription}`}>{o.description}</p>
                </Flex>
              );
            }}
            selectionRender={o => {
              return (
                <Flex align="center" gap={2}>
                  {o.isBoxemPreset && <IconBoxemB/>}
                  {o.name}
                </Flex>
              );
            }}
            onChange={v => {
              setSelectedSavedSearch(v);
              setQuery(v.query);
              clearSearch();
            }}
          />
        </div>
        {selectedSavedSearch && (
          <Flex gap={4}>
            <Button
              type="secondary"
              onClick={() => {
                clearSearch();
                savedSearches.unshift({ ...selectedSavedSearch, name: `Copy of: ${selectedSavedSearch.name}`, isBoxemPreset: false });
                setSavedSearches([...savedSearches]);
                setSelectedSavedSearch(savedSearches[0]);
              }}
            >
              <IconCopyClipboard/>
              Clone Search
            </Button>
            {allowSave && (
              <Button
                onClick={() => {
                  setSaveSearchModal({ name: selectedSavedSearch?.name ?? "", description: selectedSavedSearch?.description ?? "" });
                }}
              >
                Save as new Search
              </Button>
            )}
            {!selectedSavedSearch.isBoxemPreset && (
              <Button
                type="danger"
                onClick={() => {
                  const index = savedSearches.findIndex(ss => ss === selectedSavedSearch);
                  if (index !== -1) {
                    clearSearch();
                    savedSearches.splice(index, 1);
                    setSavedSearches([...savedSearches]);
                    setSelectedSavedSearch(null);
                  }
                }}
              >
                <IconDelete/>
                Delete Search
              </Button>
            )}
          </Flex>
        )}
      </Flex>

      <Divider/>

      <Flex gap={2} align="center" className={styles.noShrink}>
        <Tooltip
          title={""}
          children={
            <IconInfo />
          }
        />
        <h3 className={styles.noShrink}>Manual Search:</h3>
      </Flex>
        
      <Flex direction="column" gap={2}>
        {manualParams.map((mp, i) => {
          const name = mp.name;

          return (
            <Flex key={i} gap={4} align="center" className={styles.paramRow}>
              {name && <Tooltip title={""}><IconInfo/></Tooltip>}

              <label className={styles.inputGroup}>
                <Autocomplete
                  placeholder="select a product property"
                  value={name}
                  onChange={(option, index) => {
                    mp.name = option;
                    const paramRows = [...manualParams];
                    setManualParams(paramRows);
                    setQueryFromParamRows(paramRows);
                  }}
                  options={searchableParamNames}
                />
              </label>

              {name && interfaceForSearchableParam(mp, manualParams, setManualParams, setQueryFromParamRows)}

              {manualParams.length !== 1 && (
                <Button
                  type="text"
                  disabled={loading || !!queryResult?.parseError}
                  onClick={() => {
                    manualParams.splice(i, 1);
                    const paramRows = [...manualParams];
                    setManualParams(paramRows);
                    setQueryFromParamRows(paramRows);
                  }}
                >
                  <IconDelete/>
                </Button>
              )}
            </Flex>
          );
        })}
      </Flex>

      <Flex justify="end">
        <Button
          type="secondary"
          onClick={() => {
            manualParams.push(new ParamRow());
            setManualParams([...manualParams]);
          }}
        >
          <IconPlus/>
          New Search Term
        </Button>
      </Flex>

      <Divider />

      <Flex gap={2} align="center">
        <Tooltip
          title={""}
          children={
            <IconInfo />
          }
        />
        <label className={`small-heading strong ${styles.noShrink}`} htmlFor={queryTextInputId}>Advanced Search: </label>

        <Flex direction="column" className={`${styles.inputContainer}`}>
          <input
            id={queryTextInputId}
            className={`${styles.input} ${showDropdown ? styles.inputOpen : ''}`}
            placeholder="Type a search query here"
            type="text"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
            }}
          />
          {showDropdown && (() => {
            const beforeSlice = query.substring(0, queryResult.parseError.index);
            const squiggles = query.substring(queryResult.parseError.index, queryResult.parseError.index + queryResult.parseError.span);
            return (
              <Flex className={styles.hintContainer} direction="column" align="start">
                <Flex align="center" className={styles.underlineArea}>
                  <pre className={styles.pre}>{beforeSlice}</pre>
                  <span className={styles.redSquiggles}>{squiggles}</span>
                </Flex>
                <span className={styles.errorMessage}>{queryResult.parseError.message}</span>
              </Flex>
            );
          })()}
        </Flex>

        <CopyToClipBoard
          value={query}
          tooltipText="Copy advanced search to clipboard"
          tooltipPlacement="top-left"
        />
      </Flex>

      <Divider/>

      <Flex justify="end">
        <Button
          className={styles.searchButton}
          type="primary"
          size="large"
          disabled={!queryResult || !!queryResult?.parseError || loading}
          onClick={() => onSearch?.()}
        >
          <Flex align="center" gap={2}>
            <IconSearch />
            Search
          </Flex>
        </Button>
      </Flex>
    </Flex>
  );
}

class ParamRow {
  name?: string;
  gte?: number;
  lte?: number;
  checked?: boolean;
  str?: string;

  constructor() {
    this.str = "";
    this.checked = true;
    this.gte = 1;
  }
}

function interfaceForSearchableParam(
  p: ParamRow,
  manualParams: ParamRow[],
  setManualParams: (params: ParamRow[]) => void,
  setQueryFromParamRows: (params: ParamRow[]) => void,
) {
  const param = getSearchableParamByName(searchableParams.get(p.name));
  if (!param) {
    return <></>;
  }

  function interfaceForType(type: SearchableParamType) {
    switch (type) {
      case SearchableParamType.array:
        // @HACK - for now, arrays decay to their underlying type
        return interfaceForType(param.arrayType);
  
      // booleans are just a checkbox (default enabled)
      case SearchableParamType.boolean:
        return (
          <>
            <label className={styles.inputGroup}>
              <Checkbox
                value={p.checked}
                onChange={v => {
                  p.checked = v;
                  const paramRows = [...manualParams];
                  setManualParams(paramRows);
                  setQueryFromParamRows(paramRows);
                }}
              />
            </label>
          </>
        );
  
      // bytes, ints, longs. 99% have 'compare' fields,
      // where you can type a less-than-or-equals and/or -greater-than-or-equals
      // though @TODO some are just strict equality. You can perform equality
      // by making both fields the same in manual search or using '=' in advanced
      // search. 
      case SearchableParamType.byte:
      case SearchableParamType.integer:
      case SearchableParamType.long:
        return (
          <>
            <label className={styles.inputGroup}>
              <span>Greater than or equal to:</span>
              <InputNumber
                showControls={false}
                value={p.gte}
                onChange={v => {
                  p.gte = v;
                  const paramRows = [...manualParams];
                  setManualParams(paramRows);
                  setQueryFromParamRows(paramRows);
                }}
              />
            </label>
  
            <label className={styles.inputGroup}>
              <span>Less than or equal to:</span>
              <InputNumber
                showControls={false}
                value={p.lte}
                onChange={v => {
                  p.lte = v;
                  const paramRows = [...manualParams];
                  setManualParams(paramRows);
                  setQueryFromParamRows(paramRows);
                }}
              />
            </label>
          </>
        );
  
      case SearchableParamType.string:
        return (
          <label className={styles.inputGroup}>
            <TextInput
              value={p.str}
              onChange={v => {
                p.str = v;
                const paramRows = [...manualParams];
                setManualParams(paramRows);
                setQueryFromParamRows(paramRows);
              }}
            />
          </label>
        );
  
      // @TODO
  
      case SearchableParamType.date:
        break;
    }
  }

  return interfaceForType(param.type);
}

// target query:
// {
//   "current_SALES_gte": 1,
//   "current_SALES_lte": 200000,
//   "current_AMAZON_gte": -1,
//   "current_AMAZON_lte": -1,
//   "current_COUNT_NEW_gte": 3,
//   "brand": [
//       "nautica"
//   ],
//   "sort": [
//       [
//           "deltaPercent90_BUY_BOX_SHIPPING",
//           "asc"
//       ]
//   ],
//   "productType": [
//       0,
//       1,
//       2
//   ],
//   "page": 0,
//   "perPage": 50
// }


export interface SavedSearch {
  query, name, description: string;
  isBoxemPreset?: boolean;
}
const defaultSavedSearches: SavedSearch[] = [
  {
    query: "avg90_sales <= 150000 and current_rating >= 3 and outOfStockPercentage90 >= 50 and avg90_count_new >= 3 and avg90_count_new <= 100 and packageWeight <= 10000",
    name: "Good Lead",
    description: "Finds products you may want to sell. Factors in seller count, sales rank, item weight, Amazon OOS, etc. Add a brand or storefront ID to the search to find only the products you'd want to sell.", 
    isBoxemPreset: true,
    /*
      {
          "avg90_SALES_gte": 1,
          "avg90_SALES_lte": 150000,
          "current_RATING_gte": 30,
          "current_COUNT_REVIEWS_gte": 3,
          "outOfStockPercentage90_gte": 50,
          "avg90_COUNT_NEW_gte": 3,
          "avg90_COUNT_NEW_lte": 100,
          "packageWeight_gte": 1,
          "packageWeight_lte": 10000,
          "sort": [
              [
                  "current_SALES",
                  "asc"
              ]
          ],
          "lastRatingUpdate_gte": 7404159,
      }
    */
  },
  {
    query: "avg90_sales <= 50000 and current_rating >= 30 and current_count_reviews >= 3 and deltaPercent90_count_reviews = 0 and outOfStockPercentage90 >= 70 and outOfStockPercentage90 <= 99 and avg90_count_new >= 3 and avg90_count_new <= 30 and packageWeight <= 5000",
    name: "Frequent Amazon OOS",
    description: "Matches items that Amazon goes out of stock on often",
    isBoxemPreset: true,
    /*
      {
          "avg90_SALES_gte": 1,
          "avg90_SALES_lte": 50000,
          "current_RATING_gte": 30,
          "current_COUNT_REVIEWS_gte": 3,
          "deltaPercent90_COUNT_REVIEWS_gte": 0,
          "deltaPercent90_COUNT_REVIEWS_lte": 0,
          "outOfStockPercentage90_gte": 70,
          "outOfStockPercentage90_lte": 99,
          "avg90_COUNT_NEW_gte": 3,
          "avg90_COUNT_NEW_lte": 30,
          "packageWeight_gte": 1,
          "packageWeight_lte": 5000,
          "sort": [
              [
                  "current_SALES",
                  "asc"
              ]
          ],
          "lastRatingUpdate_gte": 7404160,
      }
    */
  },
  {
    query: "avg90_sales <= 100000 and deltaPercent90_buy_box_shipping >= -25 and deltaPercent90_buy_box_shipping <= 25 and outOfStockPercentage90 >= 75 and avg90_count_new >= 3 and avg90_count_new <= 50 and deltaPercent90_count_new >= 50",
    name: "Stable Price",
    description: "Find products where the buy box has been stable, but the seller count has dropped off. These leads are likely to increase in price as the sellers continue dropping off",
    isBoxemPreset: true,
    /*
      {
          "avg90_SALES_gte": 1,
          "avg90_SALES_lte": 100000,
          "deltaPercent90_BUY_BOX_SHIPPING_gte": -25,
          "deltaPercent90_BUY_BOX_SHIPPING_lte": 25,
          "current_AMAZON_gte": -1,
          "current_AMAZON_lte": -1,
          "outOfStockPercentage90_gte": 75,
          "avg90_COUNT_NEW_gte": 3,
          "avg90_COUNT_NEW_lte": 50,
          "deltaPercent90_COUNT_NEW_gte": 50,
          "sort": [
              [
                  "current_SALES",
                  "asc"
              ]
          ],
      }
    */
  },
  {
    query: "avg90_sales <= 200000 and current_count_new = 0",
    name: "High Historic Sales, Current OOS",
    description: "Products currently being sold by no-one, but their average sales rank indicates that they would sell if you brought them back to Amazon",
    isBoxemPreset: true,
    /*
      {
          "avg90_SALES_gte": 1,
          "avg90_SALES_lte": 200000,
          "current_BUY_BOX_SHIPPING_gte": -1,
          "current_BUY_BOX_SHIPPING_lte": -1,
          "current_AMAZON_gte": -1,
          "current_AMAZON_lte": -1,
          "current_NEW_gte": -1,
          "current_NEW_lte": -1,
          "current_COUNT_NEW_gte": 0,
          "current_COUNT_NEW_lte": 0,
          "sort": [
              [
                  "current_SALES",
                  "asc"
              ]
          ],
      }
    */
  },
  {
    query: "avg90_sales <= 150000 and current_rating >= 30 and current_count_reviews >= 3 and ",
    name: "Popular in FBM, but not in FBA",
    description: "These listings only have FBM competition. Many of these will be hazmat, but filtering this down to certain brands is especially valuable. Use this during Q4 to find items that you will have no problem getting buy box rotation to your FBM offer on",
    isBoxemPreset: true,
    /*
      {
          "avg90_SALES_gte": 1,
          "avg90_SALES_lte": 150000,
          "current_RATING_gte": 30,
          "current_COUNT_REVIEWS_gte": 3,
          "current_AMAZON_gte": -1,
          "current_AMAZON_lte": -1,
          "outOfStockPercentage90_gte": 75,
          "current_NEW_FBA_gte": -1,
          "current_NEW_FBA_lte": -1,
          "avg90_COUNT_NEW_gte": 3,
          "avg90_COUNT_NEW_lte": 50,
          "itemWeight_gte": 0,
          "itemWeight_lte": 7500,
          "productType": [
              "0"
          ],
          "sort": [
              [
                  "current_SALES",
                  "asc"
              ]
          ],
          "lastOffersUpdate_gte": 7529382,
          "lastRatingUpdate_gte": 7404162,
          "page": 0,
          "perPage": 50
      }
    */
  },
  {
    query: "avg90_sales <= 175000 and outOfStockPercentage90 >= 75 and avg90_count_new >= 3 and avg90_count_new <= 50 and deltaPercent90_count_new >= 50",
    name: "Accelerated Sales, Decelerated Seller Count",
    description: "Matches products that have started selling much faster in the last 90 days, and the seller count is dropping",
    isBoxemPreset: true,
    /*
      {
          "avg90_SALES_gte": 1,
          "avg90_SALES_lte": 175000,
          "deltaPercent90_SALES_gte": 50,
          "outOfStockPercentage90_gte": 75,
          "avg90_COUNT_NEW_gte": 3,
          "avg90_COUNT_NEW_lte": 50,
          "deltaPercent90_COUNT_NEW_gte": 50,
          "sort": [
              [
                  "avg90_SALES",
                  "desc"
              ]
          ],
      }
    */
  },
  {
    query: "current_sales <= 100000 and current_count_new = 0 and avg30_count_new >= 4",
    name: "Sold Out Recently",
    description: "Matches products that have sold out recently - are currently out of stock and have minimal competition. Consider adding a specific brand or category",
    isBoxemPreset: true,
    /*
      {
          "current_SALES_gte": 0,
          "current_SALES_lte": 100000,
          "current_AMAZON_gte": -1, // @TODO
          "current_AMAZON_lte": -1, // @TODO
          "current_COUNT_NEW_gte": 0,
          "current_COUNT_NEW_lte": 0,
          "avg30_COUNT_NEW_gte": 4,
          "sort": [
              [
                  "current_SALES",
                  "asc"
              ]
          ],
      }
    */
  },
  {
    query: "current_sales <= 100000 and current_amazon >= -1 and current_amazon <= -1 and brand = ",
    name: "Best in Brand",
    description: "Matches best performing products within a brand. Particularly useful for small brands",
    isBoxemPreset: true,
    /*
      {
          "current_SALES_gte": 0,
          "current_SALES_lte": 100000,
          "current_AMAZON_gte": -1,
          "current_AMAZON_lte": -1,
          "sort": [
              [
                  "current_SALES",
                  "asc"
              ]
          ],
      }
    */
  },
  {
    query: "current_sales <= 100000 and current_count_new <= 10 and avg90_count_new >= 20",
    name: "Competition Decrease",
    description: "Matches products that used to have high competition, but now it's low",
    isBoxemPreset: true,
    /*
      {
          "current_SALES_gte": 0,
          "current_SALES_lte": 100000,
          "current_AMAZON_gte": -1, // @TODO
          "current_AMAZON_lte": -1, // @TODO
          "current_COUNT_NEW_gte": 0,
          "current_COUNT_NEW_lte": 10,
          "avg90_COUNT_NEW_gte": 20,
          "sort": [
              [
                  "current_SALES",
                  "asc"
              ]
          ],
      }
    */
  }
];

const searchableParams = new Map([
  // these are the non-price-related fields:
  // the order of this map is the order of the options within the select/dropdown,
  // so it matters for UI/UX.
  ["Product name/title",                    "title"],
  // ["Category Include",                      "categories_include"], // @TODO these use integer category ids! 
  // ["Category Exclude",                      "categories_exclude"],
  ["Manufacturer",                          "manufacturer"],
  ["Brand",                                 "brand"],
  ["Brand Store Name",                      "brandStoreName"],
  ["Brand Store URL",                       "brandStoreUrlName"],
  ["Show Only One Variation per Product",   "singleVariation"],
  ["Product Group",                         "productGroup"],
  ["Part Number",                           "partNumber"],
  ["Model Name",                            "model"],
  ["Color",                                 "color"], // @TODO color dropdown
  ["Size",                                  "size"], // @TODO size dropdown
  ["Edition",                               "edition"],
  ["Format",                                "format"],
  ["Has Parent ASIN",                       "hasParentASIN"],
  ["Has Reviews",                           "hasReviews"],
  ["Package Width (cm)",                    "packageWidth"], // @TODO make it known these are in centimeters
  ["Package Length (cm)",                   "packageLength"],
  ["Package Height (cm)",                   "packageHeight"],
  ["Package Weight (grams)",                "packageWeight"], // @TODO make it known these are in grams
  ["Item Width (cm)",                       "itemWidth"], // @TODO make it known these are in centimeters
  ["Item Length (cm)",                      "itemLength"],
  ["Item Height (cm)",                      "itemHeight"],
  ["Item Weight (grams)",                   "itemWeight"],  // @TODO make it known these are in grams
  ["Variation Count",                       "variationCount"],
  ["Image Count",                           "imageCount"],
  ["Is Hazmat",                             "isHazMat"],
  ["Is Heat Sensitive",                     "isHeatSensitive"],
  ["Is Adult",                              "isAdultProduct"],
  ["Is Elligible for Trade-in",             "isEligibleForTradeIn"],
  ["Is Elligible for Super Saver Shipping", "isEligibleForSuperSaverShipping"],
  ["Is SNS",                                "isSNS"],
  ["Is Amazon Launchpad",                   "launchpad"], // @TODO what is amazon launchpad?
  ["Author",                                "author"],
  ["Binding",                               "binding"], // ex: "paperback"
  ["Genre",                                 "genre"],
  ["Languages",                             "languages"], // @TODO languages dropdown
  ["Label",                                 "label"],
  ["Publisher",                             "publisher"],
  ["Platform",                              "platform"],
  ["",                                      "activeIngredients"],
  ["",                                      "specialIngredients"],
  ["",                                      "itemTypeKeyword"],
  ["",                                      "targetAudienceKeyword"],
  ["",                                      "itemForm"],
  ["Scent",                                 "scent"], // @TODO scent dropdown
  ["Unit Type",                             "unitType"],
  ["Pattern",                               "pattern"], // @TODO pattern dropdown
  ["Style",                                 "style"], // @TODO style dropdown
  ["Material",                              "material"], // @TODO material dropdown
  ["Batteries Required",                    "batteriesRequired"],
  ["Batteries Included",                    "batteriesIncluded"],
  ["Has A+ Content",                        "hasAPlus"],
  ["A+ From Manufacturer",                  "hasAPlusFromManufacturer"],
  ["Has Main Video",                        "hasMainVideo"],
  ["Video Count",                           "videoCount"],
  ["One-time Coupon $",                     "couponOneTimeAbsolute"],
  ["One-time Coupon %",                     "couponOneTimePercent"],
  ["Subscribe & Save %",                    "couponSNSPercent"],
  ["Business Discount %",                   "businessDiscount"],
  ["Is Amazon Merch on Demand",             "isMerchOnDemand"],
  // ["", "availabilityAmazon"],
  // ["", "returnRate"],
  // ["", "trackingSince"],
  // ["", "flipability30"],
  // ["", "flipability90"],
  // ["", "flipability365"],

  // sales rank:
  ["Sales Rank current",                      "current_SALES"],
  ["Sales Rank avg. 30 days",                 "avg30_SALES"],
  ["Sales Rank avg. 90 days",                 "avg90_SALES"],
  ["Sales Rank avg. 180 days",                "avg180_SALES"],
  ["Sales Rank avg. 365 days",                "avg365_SALES"],
  ["Sales Rank 30 day delta",                 "delta30_SALES"],
  ["Sales Rank 90 day delta",                 "delta90_SALES"],
  ["Sales Rank 30 day delta %",               "deltaPercent30_SALES"],
  ["Sales Rank 90 day delta %",               "deltaPercent90_SALES"],
  ["Sales Rank lowest ever",                  "isLowest_SALES"],
  ["Sales Rank lowest 90 days",               "isLowest90_SALES"],
  // ["Sales Rank drop",                              "deltaLast_SALES"],
  // ["Sales Rank drop",                              "lastPriceChange_SALES"],
  // ["Sales Rank drop",                              "backInStock_SALES"],

  // buy-box new fields (price of the new buy box, shipping included):
  ["Buy Box New $, incl. 🚚",                      "current_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, avg. 30 days",        "avg30_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, avg. 90 days",        "avg90_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, avg. 180 days",       "avg180_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, avg. 365 days",       "avg365_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, 30 day delta",        "delta30_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, 90 day delta",        "delta90_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, 30 day delta %",      "deltaPercent30_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, 90 day delta %",      "deltaPercent90_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, lowest ever",         "isLowest_BUY_BOX_SHIPPING"],
  ["Buy Box New $, incl. 🚚, lowest 90 days",      "isLowest90_BUY_BOX_SHIPPING"],
  // ["Buy Box New 🚚, avg. 30 days", "deltaLast_BUY_BOX_SHIPPING"],
  // ["Buy Box New 🚚, avg. 30 days", "backInStock_BUY_BOX_SHIPPING"],

  // ["Buy Box New 🚚, avg. 30 days", "lastPriceChange_BUY_BOX_SHIPPING"],

  // Sold and fulfilled by Amazon (the listing is owned by amazon). Gaps in the graph are periods Amazon did not offer the product (was out of stock).
  ["Amazon $ current",                              "current_AMAZON"],
  ["Amazon $ avg. 30 days",                         "avg30_AMAZON"],
  ["Amazon $ avg. 90 days",                         "avg90_AMAZON"],
  ["Amazon $ avg. 180 days",                        "avg180_AMAZON"],
  ["Amazon $ avg. 365 days",                        "avg365_AMAZON"],
  ["Amazon $, 30 day delta",                        "delta30_AMAZON"],
  ["Amazon $, 90 day delta",                        "delta90_AMAZON"],
  ["Amazon $, 30 day delta %",                      "deltaPercent90_AMAZON"],
  ["Amazon $, 90 day delta %",                      "deltaPercent30_AMAZON"],

  ["", "deltaLast_AMAZON"],
  ["", "lastPriceChange_AMAZON"],
  ["", "backInStock_AMAZON"],
  ["", "isLowest_AMAZON"],
  ["", "isLowest90_AMAZON"],
  // ["", "lastPriceChange"], // @TODO date
  // ["", "lastOffersUpdate"],
  // ["Is Lowest Offer?", "isLowestOffer"],

  // buy box flags:
  ["Buy Box is Preorder",                              "buyBoxIsPreorder"],
  ["Buy Box is Backorder",                             "buyBoxIsBackorder"],
  ["Buy Box is Prime Exclusive",                       "buyBoxIsPrimeExclusive"],
  ["Buy Box is Amazon-Owned",                          "buyBoxIsAmazon"],
  ["Buy Box is FBA",                                   "buyBoxIsFBA"],
  ["Buy Box is Unqualified",                           "buyBoxIsUnqualified"],
  ["Buy Box New Seller ID",                            "buyBoxSellerId"],
  ["Buy Box is Used Condition",                        "buyBoxUsedCondition"],
  ["Buy Box Used is FBA",                              "buyBoxUsedIsFBA"],
  ["Buy Box Used Seller ID",                           "buyBoxUsedSellerId"],

  // offers:
  ["Total Offer Count",                                "totalOfferCount"],
  ["Total FBA Offer Count",                            "offerCountFBA"],
  ["Total FBM Offer Count",                            "offerCountFBM"],

  ["New Offer Count",                                  "current_COUNT_NEW"],
  ["New Offer Count avg. 30 days",                     "avg30_COUNT_NEW"],
  ["New Offer Count avg. 90 days",                     "avg90_COUNT_NEW"],
  ["New Offer Count avg. 180 days",                    "avg180_COUNT_NEW"],
  ["New Offer Count avg. 365 days",                    "avg365_COUNT_NEW"],
  ["New Offer Count 30 day delta",                     "delta30_COUNT_NEW"],
  ["New Offer Count 90 day delta",                     "delta90_COUNT_NEW"],
  ["New Offer Count 30 day delta percent",             "deltaPercent30_COUNT_NEW"],
  ["New Offer Count 90 day delta percent",             "deltaPercent90_COUNT_NEW"],
  ["New Offer Count lowest ever",                      "isLowest_COUNT_NEW"],
  ["New Offer Count lowest 90 days",                   "isLowest90_COUNT_NEW"],
  ["",                                                 "lastPriceChange_COUNT_NEW"],
  ["",                                                 "backInStock_COUNT_NEW"],
  ["",                                                 "deltaLast_COUNT_NEW"],

  ["Used Offer Count",                                 "current_COUNT_USED"],
  ["Used Offer Count avg. 30 days",                    "avg30_COUNT_USED"],
  ["Used Offer Count avg. 90 days",                    "avg90_COUNT_USED"],
  ["Used Offer Count avg. 180 days",                   "avg180_COUNT_USED"],
  ["Used Offer Count avg. 365 days",                   "avg365_COUNT_USED"],
  ["Used Offer Count avg. 30 day delta",               "delta30_COUNT_USED"],
  ["Used Offer Count avg. 90 day delta",               "delta90_COUNT_USED"],
  ["Used Offer Count avg. 30 day delta %",             "deltaPercent30_COUNT_USED"],
  ["Used Offer Count avg. 90 day delta %",             "deltaPercent90_COUNT_USED"],
  ["Used Offer Count lowest ever",                     "isLowest_COUNT_USED"],
  ["Used Offer Count lowest 90 days",                  "isLowest90_COUNT_USED"],
  ["",                                                 "deltaLast_COUNT_USED"],
  ["",                                                 "lastPriceChange_COUNT_USED"],
  ["",                                                 "backInStock_COUNT_USED"],

  ["Refurbished Offer Count",                          "current_COUNT_REFURBISHED"],
  ["Refurbished Offer Count avg. 30 days",             "avg30_COUNT_REFURBISHED"],
  ["Refurbished Offer Count avg. 90 days",             "avg90_COUNT_REFURBISHED"],
  ["Refurbished Offer Count avg. 180 days",            "avg180_COUNT_REFURBISHED"],
  ["Refurbished Offer Count avg. 365 days",            "avg365_COUNT_REFURBISHED"],
  ["Refurbished Offer Count avg. 30 day delta",        "delta30_COUNT_REFURBISHED"],
  ["Refurbished Offer Count avg. 90 day delta",        "delta90_COUNT_REFURBISHED"],
  ["Refurbished Offer Count avg. 30 day delta %",      "deltaPercent30_COUNT_REFURBISHED"],
  ["Refurbished Offer Count avg. 30 day delta %",      "deltaPercent90_COUNT_REFURBISHED"],
  ["Refurbished Offer Count lowest ever",              "isLowest_COUNT_REFURBISHED"],
  ["Refurbished Offer Count lowest 90 days",           "isLowest90_COUNT_REFURBISHED"],
  ["",                                                 "deltaLast_COUNT_REFURBISHED"],
  ["",                                                 "lastPriceChange_COUNT_REFURBISHED"],
  ["",                                                 "backInStock_COUNT_REFURBISHED"],

  ["Collectible Offer Count",                          "current_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count avg. 30 days",             "avg30_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count avg. 90 days",             "avg90_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count avg. 180 days",            "avg180_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count avg. 365 days",            "avg365_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count avg. 30 day delta",        "delta30_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count avg. 90 day delta",        "delta90_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count avg. 30 day delta %",      "deltaPercent30_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count avg. 90 day delta %",      "deltaPercent90_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count lowest ever",              "isLowest_COUNT_COLLECTIBLE"],
  ["Collectible Offer Count lowest 90 days",           "isLowest90_COUNT_COLLECTIBLE"],
  ["", "deltaLast_COUNT_COLLECTIBLE"],
  ["", "lastPriceChange_COUNT_COLLECTIBLE"],
  ["", "backInStock_COUNT_COLLECTIBLE"],

  ["", "current_NEW"],
  ["", "current_USED"],
  ["", "current_LISTPRICE"],
  ["", "current_COLLECTIBLE"],
  ["", "current_REFURBISHED"],
  ["", "current_NEW_FBM_SHIPPING"],
  ["", "current_LIGHTNING_DEAL"],
  ["", "current_WAREHOUSE"],
  ["", "current_NEW_FBA"],
  ["", "avg365_COLLECTIBLE"],

  ["", "current_USED_NEW_SHIPPING"],
  ["", "current_USED_VERY_GOOD_SHIPPING"],
  ["", "current_USED_GOOD_SHIPPING"],
  ["", "current_USED_ACCEPTABLE_SHIPPING"],
  ["", "current_REFURBISHED_SHIPPING"],
  ["", "current_TRADE_IN"],

  // ["Review Count", "current_COUNT_REVIEWS"],
  // ["", "current_RATING"],
  ["", "variationReviewCount"],
  ["", "variationRatingCount"],

  ["", "buyBoxStatsAmazon30"],
  ["", "buyBoxStatsAmazon90"],
  ["", "buyBoxStatsAmazon180"],
  ["", "buyBoxStatsAmazon365"],
  ["", "buyBoxStatsTopSeller30"],
  ["", "buyBoxStatsTopSeller90"],
  ["", "buyBoxStatsTopSeller180"],
  ["", "buyBoxStatsTopSeller365"],
  ["", "buyBoxStatsSellerCount30"],
  ["", "buyBoxStatsSellerCount90"],
  ["", "buyBoxStatsSellerCount180"],
  ["", "buyBoxStatsSellerCount365"],
  ["", "outOfStockPercentage90"],

  ["", "deltaPercent90_monthlySold"],
  ["", "outOfStockCountAmazon30"],
  ["", "outOfStockCountAmazon90"],
 
  ["", "sellerIds"],
  ["", "sellerIdsLowestFBA"],
  ["", "sellerIdsLowestFBM"],
  ["", "numberOfItems"],
  ["Number of Pages", "numberOfPages"],
  ["", "publicationDate"],
  ["", "releaseDate"],
  ["Is Prime Exclusive", "isPrimeExclusive"],
  ["", "lightningEnd"],
  ["", "monthlySold"],

  ["", "delta90_NEW"],
  ["", "delta90_USED"],
  ["", "delta90_LISTPRICE"],
  ["", "delta90_COLLECTIBLE"],
  ["", "delta90_REFURBISHED"],
  ["", "delta90_NEW_FBM_SHIPPING"],
  ["", "delta90_LIGHTNING_DEAL"],
  ["", "delta90_WAREHOUSE"],
  ["", "delta90_NEW_FBA"],

  ["", "delta90_RATING"],
  ["", "delta90_COUNT_REVIEWS"],

  ["", "delta90_USED_NEW_SHIPPING"],
  ["", "delta90_USED_VERY_GOOD_SHIPPING"],
  ["", "delta90_USED_GOOD_SHIPPING"],
  ["", "delta90_USED_ACCEPTABLE_SHIPPING"],
  ["", "delta90_REFURBISHED_SHIPPING"],
  ["", "delta90_TRADE_IN"],

  ["", "delta30_NEW"],
  ["", "delta30_USED"],

  ["", "delta30_LISTPRICE"],
  ["", "delta30_COLLECTIBLE"],
  ["", "delta30_REFURBISHED"],
  ["", "delta30_NEW_FBM_SHIPPING"],
  ["", "delta30_LIGHTNING_DEAL"],
  ["", "delta30_WAREHOUSE"],
  ["", "delta30_NEW_FBA"],

  ["", "delta30_RATING"],
  ["", "delta30_COUNT_REVIEWS"],

  ["", "delta30_USED_NEW_SHIPPING"],
  ["", "delta30_USED_VERY_GOOD_SHIPPING"],
  ["", "delta30_USED_GOOD_SHIPPING"],
  ["", "delta30_USED_ACCEPTABLE_SHIPPING"],
  ["", "delta30_REFURBISHED_SHIPPING"],
  ["", "delta30_TRADE_IN"],

  ["", "deltaPercent90_NEW"],
  ["", "deltaPercent90_USED"],

  ["", "deltaPercent90_LISTPRICE"],
  ["", "deltaPercent90_COLLECTIBLE"],
  ["", "deltaPercent90_REFURBISHED"],
  ["", "deltaPercent90_NEW_FBM_SHIPPING"],
  ["", "deltaPercent90_LIGHTNING_DEAL"],
  ["", "deltaPercent90_WAREHOUSE"],
  ["", "deltaPercent90_NEW_FBA"],

  ["", "deltaPercent90_RATING"],
  ["", "deltaPercent90_COUNT_REVIEWS"],

  ["", "deltaPercent90_USED_NEW_SHIPPING"],
  ["", "deltaPercent90_USED_VERY_GOOD_SHIPPING"],
  ["", "deltaPercent90_USED_GOOD_SHIPPING"],
  ["", "deltaPercent90_USED_ACCEPTABLE_SHIPPING"],
  ["", "deltaPercent90_REFURBISHED_SHIPPING"],
  ["", "deltaPercent90_TRADE_IN"],

  ["", "deltaPercent30_NEW"],
  ["", "deltaPercent30_USED"],

  ["", "deltaPercent30_LISTPRICE"],
  ["", "deltaPercent30_COLLECTIBLE"],
  ["", "deltaPercent30_REFURBISHED"],
  ["", "deltaPercent30_NEW_FBM_SHIPPING"],
  ["", "deltaPercent30_LIGHTNING_DEAL"],
  ["", "deltaPercent30_WAREHOUSE"],
  ["", "deltaPercent30_NEW_FBA"],

  ["", "deltaPercent30_RATING"],
  ["", "deltaPercent30_COUNT_REVIEWS"],

  ["", "deltaPercent30_USED_NEW_SHIPPING"],
  ["", "deltaPercent30_USED_VERY_GOOD_SHIPPING"],
  ["", "deltaPercent30_USED_GOOD_SHIPPING"],
  ["", "deltaPercent30_USED_ACCEPTABLE_SHIPPING"],
  ["", "deltaPercent30_REFURBISHED_SHIPPING"],
  ["", "deltaPercent30_TRADE_IN"],

  ["", "deltaLast_NEW"],
  ["", "deltaLast_USED"],

  ["", "deltaLast_LISTPRICE"],
  ["", "deltaLast_COLLECTIBLE"],
  ["", "deltaLast_REFURBISHED"],
  ["", "deltaLast_NEW_FBM_SHIPPING"],
  ["", "deltaLast_LIGHTNING_DEAL"],
  ["", "deltaLast_WAREHOUSE"],
  ["", "deltaLast_NEW_FBA"],

  ["", "deltaLast_RATING"],
  ["", "deltaLast_COUNT_REVIEWS"],

  ["", "deltaLast_USED_NEW_SHIPPING"],
  ["", "deltaLast_USED_VERY_GOOD_SHIPPING"],
  ["", "deltaLast_USED_GOOD_SHIPPING"],
  ["", "deltaLast_USED_ACCEPTABLE_SHIPPING"],
  ["", "deltaLast_REFURBISHED_SHIPPING"],
  ["", "deltaLast_TRADE_IN"],

  ["", "avg180_NEW"],
  ["", "avg180_USED"],

  ["", "avg180_LISTPRICE"],
  ["", "avg180_COLLECTIBLE"],
  ["", "avg180_REFURBISHED"],
  ["", "avg180_NEW_FBM_SHIPPING"],
  ["", "avg180_LIGHTNING_DEAL"],
  ["", "avg180_WAREHOUSE"],
  ["", "avg180_NEW_FBA"],

  ["", "avg180_RATING"],
  ["", "avg180_COUNT_REVIEWS"],

  ["", "avg180_USED_NEW_SHIPPING"],
  ["", "avg180_USED_VERY_GOOD_SHIPPING"],
  ["", "avg180_USED_GOOD_SHIPPING"],
  ["", "avg180_USED_ACCEPTABLE_SHIPPING"],
  ["", "avg180_REFURBISHED_SHIPPING"],
  ["", "avg180_TRADE_IN"],

  ["", "avg90_NEW"],
  ["", "avg90_USED"],

  ["", "avg90_LISTPRICE"],
  ["", "avg90_COLLECTIBLE"],
  ["", "avg90_REFURBISHED"],
  ["", "avg90_NEW_FBM_SHIPPING"],
  ["", "avg90_LIGHTNING_DEAL"],
  ["", "avg90_WAREHOUSE"],
  ["", "avg90_NEW_FBA"],

  ["", "avg90_RATING"],
  ["", "avg90_COUNT_REVIEWS"],

  ["", "avg90_USED_NEW_SHIPPING"],
  ["", "avg90_USED_VERY_GOOD_SHIPPING"],
  ["", "avg90_USED_GOOD_SHIPPING"],
  ["", "avg90_USED_ACCEPTABLE_SHIPPING"],
  ["", "avg90_REFURBISHED_SHIPPING"],
  ["", "avg90_TRADE_IN"],

  ["", "avg30_NEW"],
  ["", "avg30_USED"],

  ["", "avg30_LISTPRICE"],
  ["", "avg30_COLLECTIBLE"],
  ["", "avg30_REFURBISHED"],
  ["", "avg30_NEW_FBM_SHIPPING"],
  ["", "avg30_LIGHTNING_DEAL"],
  ["", "avg30_WAREHOUSE"],
  ["", "avg30_NEW_FBA"],

  ["", "avg30_RATING"],
  ["", "avg30_COUNT_REVIEWS"],

  ["", "avg30_USED_NEW_SHIPPING"],
  ["", "avg30_USED_VERY_GOOD_SHIPPING"],
  ["", "avg30_USED_GOOD_SHIPPING"],
  ["", "avg30_USED_ACCEPTABLE_SHIPPING"],
  ["", "avg30_REFURBISHED_SHIPPING"],
  ["", "avg30_TRADE_IN"],


  ["", "avg365_BUY_BOX_USED_SHIPPING"],

  ["", "avg365_COUNT_REVIEWS"],
  ["", "avg365_EBAY_NEW_SHIPPING"],
  ["", "avg365_EBAY_USED_SHIPPING"],
  ["", "avg365_LIGHTNING_DEAL"],
  ["", "avg365_LISTPRICE"],
  ["", "avg365_NEW"],
  ["", "avg365_NEW_FBA"],
  ["", "avg365_NEW_FBM_SHIPPING"],
  ["", "avg365_PRIME_EXCL"],
  ["", "avg365_RATING"],
  ["", "avg365_REFURBISHED"],
  ["", "avg365_REFURBISHED_SHIPPING"],
  ["", "avg365_RENT"],

  ["", "avg365_TRADE_IN"],
  ["", "avg365_USED"],
  ["", "avg365_USED_ACCEPTABLE_SHIPPING"],
  ["", "avg365_USED_GOOD_SHIPPING"],
  ["", "avg365_USED_NEW_SHIPPING"],
  ["", "avg365_USED_VERY_GOOD_SHIPPING"],
  ["", "avg365_WAREHOUSE"],

  ["", "lastPriceChange_NEW"],
  ["", "lastPriceChange_USED"],

  ["", "lastPriceChange_LISTPRICE"],
  ["", "lastPriceChange_COLLECTIBLE"],
  ["", "lastPriceChange_REFURBISHED"],
  ["", "lastPriceChange_NEW_FBM_SHIPPING"],
  ["", "lastPriceChange_LIGHTNING_DEAL"],
  ["", "lastPriceChange_WAREHOUSE"],
  ["", "lastPriceChange_NEW_FBA"],

  ["", "lastPriceChange_RATING"],
  ["", "lastPriceChange_COUNT_REVIEWS"],

  ["", "lastPriceChange_USED_NEW_SHIPPING"],
  ["", "lastPriceChange_USED_VERY_GOOD_SHIPPING"],
  ["", "lastPriceChange_USED_GOOD_SHIPPING"],
  ["", "lastPriceChange_USED_ACCEPTABLE_SHIPPING"],
  ["", "lastPriceChange_REFURBISHED_SHIPPING"],
  ["", "lastPriceChange_EBAY_NEW_SHIPPING"],
  ["", "lastPriceChange_EBAY_USED_SHIPPING"],
  ["", "lastPriceChange_TRADE_IN"],
  ["", "lastPriceChange_RENT"],
  ["", "lastPriceChange_BUY_BOX_USED_SHIPPING"],
  ["", "lastPriceChange_PRIME_EXCL"],

  ["", "backInStock_NEW"],
  ["", "backInStock_USED"],

  ["", "backInStock_LISTPRICE"],
  ["", "backInStock_COLLECTIBLE"],
  ["", "backInStock_REFURBISHED"],
  ["", "backInStock_NEW_FBM_SHIPPING"],
  ["", "backInStock_LIGHTNING_DEAL"],
  ["", "backInStock_WAREHOUSE"],
  ["", "backInStock_NEW_FBA"],

  ["", "backInStock_RATING"],
  ["", "backInStock_COUNT_REVIEWS"],
  ["", "backInStock_USED_NEW_SHIPPING"],
  ["", "backInStock_USED_VERY_GOOD_SHIPPING"],
  ["", "backInStock_USED_GOOD_SHIPPING"],
  ["", "backInStock_USED_ACCEPTABLE_SHIPPING"],
  ["", "backInStock_REFURBISHED_SHIPPING"],
  ["", "backInStock_TRADE_IN"],


  ["", "isLowest_BUY_BOX_USED_SHIPPING"],
  ["", "isLowest_COLLECTIBLE"],

  
  ["", "isLowest_COUNT_REVIEWS"],
  ["", "isLowest_EBAY_NEW_SHIPPING"],
  ["", "isLowest_EBAY_USED_SHIPPING"],
  ["", "isLowest_LIGHTNING_DEAL"],
  ["", "isLowest_LISTPRICE"],
  ["", "isLowest_NEW"],
  ["", "isLowest_NEW_FBA"],
  ["", "isLowest_NEW_FBM_SHIPPING"],
  ["", "isLowest_PRIME_EXCL"],
  ["", "isLowest_RATING"],
  ["", "isLowest_REFURBISHED"],
  ["", "isLowest_REFURBISHED_SHIPPING"],
  ["", "isLowest_RENT"],

  ["", "isLowest_TRADE_IN"],
  ["", "isLowest_USED"],
  ["", "isLowest_USED_ACCEPTABLE_SHIPPING"],
  ["", "isLowest_USED_GOOD_SHIPPING"],
  ["", "isLowest_USED_NEW_SHIPPING"],
  ["", "isLowest_USED_VERY_GOOD_SHIPPING"],
  ["", "isLowest_WAREHOUSE"],


  ["", "isLowest90_BUY_BOX_USED_SHIPPING"],
  ["", "isLowest90_COLLECTIBLE"],


  ["", "isLowest90_COUNT_REVIEWS"],
  ["", "isLowest90_EBAY_NEW_SHIPPING"],
  ["", "isLowest90_EBAY_USED_SHIPPING"],
  ["", "isLowest90_LIGHTNING_DEAL"],
  ["", "isLowest90_LISTPRICE"],
  ["", "isLowest90_NEW"],
  ["", "isLowest90_NEW_FBA"],
  ["", "isLowest90_NEW_FBM_SHIPPING"],
  ["", "isLowest90_PRIME_EXCL"],
  ["", "isLowest90_RATING"],
  ["", "isLowest90_REFURBISHED"],
  ["", "isLowest90_REFURBISHED_SHIPPING"],
  ["", "isLowest90_RENT"],

  ["", "isLowest90_TRADE_IN"],
  ["", "isLowest90_USED"],
  ["", "isLowest90_USED_ACCEPTABLE_SHIPPING"],
  ["", "isLowest90_USED_GOOD_SHIPPING"],
  ["", "isLowest90_USED_NEW_SHIPPING"],
  ["", "isLowest90_USED_VERY_GOOD_SHIPPING"],
  ["", "isLowest90_WAREHOUSE"],
]);
const searchableParamMapKeys = Array.from(searchableParams.keys());
const searchableParamNames = searchableParamMapKeys.filter(k => !!k && k !== "");
const searchableParamNamesToNiceName = searchableParamNames.reduce((acc, curr) => {
  acc.set(searchableParams.get(curr), curr);
  return acc;
}, new Map<string, string>());

// @TODO go through here and write tooltips/descriptions for each property: https://keepa.com/#!discuss/t/product-finder/5473
const Tooltips = {
  // "page": new SearchableParam<number>({ type: SearchableParamType.integer }),
  // "perPage": new SearchableParam<number>({ type: SearchableParamType.integer }),
  // "rootCategory": new SearchableParam<number>(false),
  // "salesRankReference": new SearchableParamLong(false),
  // "productType": new SearchableParamInteger(false),

  "categories_include": "",
  "categories_exclude": "",
  "manufacturer": "",
  "title": "",
  "singleVariation": "",
  "lastPriceChange": "",
  "lastOffersUpdate": "",
  "isLowestOffer": "",
  "hasParentASIN": "",
  "availabilityAmazon": "",
  "returnRate": "",
  "hasReviews": "",
  "trackingSince": "",
  "brand": "",
  "productGroup": "",
  "partNumber": "",
  "model": "",
  "color": "",
  "size": "",
  "edition": "",
  "format": "",
  "packageHeight": "",
  "packageLength": "",
  "packageWidth": "",
  "packageWeight": "",
  "itemHeight": "",
  "itemLength": "",
  "itemWidth": "",
  "itemWeight": "",
  "variationCount": "",
  "imageCount": "",
  "buyBoxStatsAmazon30": "",
  "buyBoxStatsAmazon90": "",
  "buyBoxStatsAmazon180": "",
  "buyBoxStatsAmazon365": "",
  "buyBoxStatsTopSeller30": "",
  "buyBoxStatsTopSeller90": "",
  "buyBoxStatsTopSeller180": "",
  "buyBoxStatsTopSeller365": "",
  "buyBoxStatsSellerCount30": "",
  "buyBoxStatsSellerCount90": "",
  "buyBoxStatsSellerCount180": "",
  "buyBoxStatsSellerCount365": "",
  "outOfStockPercentage90": "",
  "variationReviewCount": "",
  "variationRatingCount": "",
  "deltaPercent90_monthlySold": "",
  "outOfStockCountAmazon30": "",
  "outOfStockCountAmazon90": "",
  "isHazMat": "",
  "isHeatSensitive": "",
  "isAdultProduct": "",
  "isEligibleForTradeIn": "",
  "isEligibleForSuperSaverShipping": "",
  "isSNS": "",
  "launchpad": "",
  "buyBoxIsPreorder": "",
  "buyBoxIsBackorder": "",
  "buyBoxIsPrimeExclusive": "",
  "author": "",
  "binding": "",
  "genre": "",
  "languages": "",
  "label": "",
  "publisher": "",
  "platform": "",
  "activeIngredients": "",
  "specialIngredients": "",
  "itemTypeKeyword": "",
  "targetAudienceKeyword": "",
  "itemForm": "",
  "scent": "",
  "unitType": "",
  "pattern": "",
  "style": "",
  "material": "",
  "couponOneTimeAbsolute": "",
  "couponOneTimePercent": "",
  "couponSNSPercent": "",
  "flipability30": "",
  "flipability90": "",
  "flipability365": "",
  "businessDiscount": "",
  "batteriesRequired": "",
  "batteriesIncluded": "",
  "isMerchOnDemand": "",
  "hasMainVideo": "",
  "hasAPlus": "",
  "hasAPlusFromManufacturer": "",
  "videoCount": "",
  "brandStoreName": "",
  "brandStoreUrlName": "",
  "buyBoxIsAmazon": "",
  "buyBoxIsFBA": "",
  "buyBoxIsUnqualified": "",
  "buyBoxSellerId": "",
  "buyBoxUsedCondition": "",
  "buyBoxUsedIsFBA": "",
  "buyBoxUsedSellerId": "",
  "sellerIds": "",
  "sellerIdsLowestFBA": "",
  "sellerIdsLowestFBM": "",
  "numberOfItems": "",
  "numberOfPages": "",
  "publicationDate": "",
  "releaseDate": "",
  "isPrimeExclusive": "",
  "lightningEnd": "",
  "monthlySold": "",
  "totalOfferCount": "",
  "offerCountFBA": "",
  "offerCountFBM": "",
  "current_AMAZON": "",
  "current_NEW": "",
  "current_USED": "",
  "current_SALES": "",
  "current_LISTPRICE": "",
  "current_COLLECTIBLE": "",
  "current_REFURBISHED": "",
  "current_NEW_FBM_SHIPPING": "",
  "current_LIGHTNING_DEAL": "",
  "current_WAREHOUSE": "",
  "current_NEW_FBA": "",
  "current_COUNT_NEW": "",
  "current_COUNT_USED": "",
  "current_COUNT_REFURBISHED": "",
  "current_COUNT_COLLECTIBLE": "",
  "current_RATING": "",
  "current_COUNT_REVIEWS": "",
  "current_BUY_BOX_SHIPPING": "",
  "current_USED_NEW_SHIPPING": "",
  "current_USED_VERY_GOOD_SHIPPING": "",
  "current_USED_GOOD_SHIPPING": "",
  "current_USED_ACCEPTABLE_SHIPPING": "",
  "current_REFURBISHED_SHIPPING": "",
  "current_TRADE_IN": "",
  "delta90_AMAZON": "",
  "delta90_NEW": "",
  "delta90_USED": "",
  "delta90_SALES": "",
  "delta90_LISTPRICE": "",
  "delta90_COLLECTIBLE": "",
  "delta90_REFURBISHED": "",
  "delta90_NEW_FBM_SHIPPING": "",
  "delta90_LIGHTNING_DEAL": "",
  "delta90_WAREHOUSE": "",
  "delta90_NEW_FBA": "",
  "delta90_COUNT_NEW": "",
  "delta90_COUNT_USED": "",
  "delta90_COUNT_REFURBISHED": "",
  "delta90_COUNT_COLLECTIBLE": "",
  "delta90_RATING": "",
  "delta90_COUNT_REVIEWS": "",
  "delta90_BUY_BOX_SHIPPING": "",
  "delta90_USED_NEW_SHIPPING": "",
  "delta90_USED_VERY_GOOD_SHIPPING": "",
  "delta90_USED_GOOD_SHIPPING": "",
  "delta90_USED_ACCEPTABLE_SHIPPING": "",
  "delta90_REFURBISHED_SHIPPING": "",
  "delta90_TRADE_IN": "",
  "delta30_AMAZON": "",
  "delta30_NEW": "",
  "delta30_USED": "",
  "delta30_SALES": "",
  "delta30_LISTPRICE": "",
  "delta30_COLLECTIBLE": "",
  "delta30_REFURBISHED": "",
  "delta30_NEW_FBM_SHIPPING": "",
  "delta30_LIGHTNING_DEAL": "",
  "delta30_WAREHOUSE": "",
  "delta30_NEW_FBA": "",
  "delta30_COUNT_NEW": "",
  "delta30_COUNT_USED": "",
  "delta30_COUNT_REFURBISHED": "",
  "delta30_COUNT_COLLECTIBLE": "",
  "delta30_RATING": "",
  "delta30_COUNT_REVIEWS": "",
  "delta30_BUY_BOX_SHIPPING": "",
  "delta30_USED_NEW_SHIPPING": "",
  "delta30_USED_VERY_GOOD_SHIPPING": "",
  "delta30_USED_GOOD_SHIPPING": "",
  "delta30_USED_ACCEPTABLE_SHIPPING": "",
  "delta30_REFURBISHED_SHIPPING": "",
  "delta30_TRADE_IN": "",
  "deltaPercent90_AMAZON": "",
  "deltaPercent90_NEW": "",
  "deltaPercent90_USED": "",
  "deltaPercent90_SALES": "",
  "deltaPercent90_LISTPRICE": "",
  "deltaPercent90_COLLECTIBLE": "",
  "deltaPercent90_REFURBISHED": "",
  "deltaPercent90_NEW_FBM_SHIPPING": "",
  "deltaPercent90_LIGHTNING_DEAL": "",
  "deltaPercent90_WAREHOUSE": "",
  "deltaPercent90_NEW_FBA": "",
  "deltaPercent90_COUNT_NEW": "",
  "deltaPercent90_COUNT_USED": "",
  "deltaPercent90_COUNT_REFURBISHED": "",
  "deltaPercent90_COUNT_COLLECTIBLE": "",
  "deltaPercent90_RATING": "",
  "deltaPercent90_COUNT_REVIEWS": "",
  "deltaPercent90_BUY_BOX_SHIPPING": "",
  "deltaPercent90_USED_NEW_SHIPPING": "",
  "deltaPercent90_USED_VERY_GOOD_SHIPPING": "",
  "deltaPercent90_USED_GOOD_SHIPPING": "",
  "deltaPercent90_USED_ACCEPTABLE_SHIPPING": "",
  "deltaPercent90_REFURBISHED_SHIPPING": "",
  "deltaPercent90_TRADE_IN": "",
  "deltaPercent30_AMAZON": "",
  "deltaPercent30_NEW": "",
  "deltaPercent30_USED": "",
  "deltaPercent30_SALES": "",
  "deltaPercent30_LISTPRICE": "",
  "deltaPercent30_COLLECTIBLE": "",
  "deltaPercent30_REFURBISHED": "",
  "deltaPercent30_NEW_FBM_SHIPPING": "",
  "deltaPercent30_LIGHTNING_DEAL": "",
  "deltaPercent30_WAREHOUSE": "",
  "deltaPercent30_NEW_FBA": "",
  "deltaPercent30_COUNT_NEW": "",
  "deltaPercent30_COUNT_USED": "",
  "deltaPercent30_COUNT_REFURBISHED": "",
  "deltaPercent30_COUNT_COLLECTIBLE": "",
  "deltaPercent30_RATING": "",
  "deltaPercent30_COUNT_REVIEWS": "",
  "deltaPercent30_BUY_BOX_SHIPPING": "",
  "deltaPercent30_USED_NEW_SHIPPING": "",
  "deltaPercent30_USED_VERY_GOOD_SHIPPING": "",
  "deltaPercent30_USED_GOOD_SHIPPING": "",
  "deltaPercent30_USED_ACCEPTABLE_SHIPPING": "",
  "deltaPercent30_REFURBISHED_SHIPPING": "",
  "deltaPercent30_TRADE_IN": "",
  "deltaLast_AMAZON": "",
  "deltaLast_NEW": "",
  "deltaLast_USED": "",
  "deltaLast_SALES": "",
  "deltaLast_LISTPRICE": "",
  "deltaLast_COLLECTIBLE": "",
  "deltaLast_REFURBISHED": "",
  "deltaLast_NEW_FBM_SHIPPING": "",
  "deltaLast_LIGHTNING_DEAL": "",
  "deltaLast_WAREHOUSE": "",
  "deltaLast_NEW_FBA": "",
  "deltaLast_COUNT_NEW": "",
  "deltaLast_COUNT_USED": "",
  "deltaLast_COUNT_REFURBISHED": "",
  "deltaLast_COUNT_COLLECTIBLE": "",
  "deltaLast_RATING": "",
  "deltaLast_COUNT_REVIEWS": "",
  "deltaLast_BUY_BOX_SHIPPING": "",
  "deltaLast_USED_NEW_SHIPPING": "",
  "deltaLast_USED_VERY_GOOD_SHIPPING": "",
  "deltaLast_USED_GOOD_SHIPPING": "",
  "deltaLast_USED_ACCEPTABLE_SHIPPING": "",
  "deltaLast_REFURBISHED_SHIPPING": "",
  "deltaLast_TRADE_IN": "",
  "avg180_AMAZON": "",
  "avg180_NEW": "",
  "avg180_USED": "",
  "avg180_SALES": "",
  "avg180_LISTPRICE": "",
  "avg180_COLLECTIBLE": "",
  "avg180_REFURBISHED": "",
  "avg180_NEW_FBM_SHIPPING": "",
  "avg180_LIGHTNING_DEAL": "",
  "avg180_WAREHOUSE": "",
  "avg180_NEW_FBA": "",
  "avg180_COUNT_NEW": "",
  "avg180_COUNT_USED": "",
  "avg180_COUNT_REFURBISHED": "",
  "avg180_COUNT_COLLECTIBLE": "",
  "avg180_RATING": "",
  "avg180_COUNT_REVIEWS": "",
  "avg180_BUY_BOX_SHIPPING": "",
  "avg180_USED_NEW_SHIPPING": "",
  "avg180_USED_VERY_GOOD_SHIPPING": "",
  "avg180_USED_GOOD_SHIPPING": "",
  "avg180_USED_ACCEPTABLE_SHIPPING": "",
  "avg180_REFURBISHED_SHIPPING": "",
  "avg180_TRADE_IN": "",
  "avg90_AMAZON": "",
  "avg90_NEW": "",
  "avg90_USED": "",
  "avg90_SALES": "",
  "avg90_LISTPRICE": "",
  "avg90_COLLECTIBLE": "",
  "avg90_REFURBISHED": "",
  "avg90_NEW_FBM_SHIPPING": "",
  "avg90_LIGHTNING_DEAL": "",
  "avg90_WAREHOUSE": "",
  "avg90_NEW_FBA": "",
  "avg90_COUNT_NEW": "",
  "avg90_COUNT_USED": "",
  "avg90_COUNT_REFURBISHED": "",
  "avg90_COUNT_COLLECTIBLE": "",
  "avg90_RATING": "",
  "avg90_COUNT_REVIEWS": "",
  "avg90_BUY_BOX_SHIPPING": "",
  "avg90_USED_NEW_SHIPPING": "",
  "avg90_USED_VERY_GOOD_SHIPPING": "",
  "avg90_USED_GOOD_SHIPPING": "",
  "avg90_USED_ACCEPTABLE_SHIPPING": "",
  "avg90_REFURBISHED_SHIPPING": "",
  "avg90_TRADE_IN": "",
  "avg30_AMAZON": "",
  "avg30_NEW": "",
  "avg30_USED": "",
  "avg30_SALES": "",
  "avg30_LISTPRICE": "",
  "avg30_COLLECTIBLE": "",
  "avg30_REFURBISHED": "",
  "avg30_NEW_FBM_SHIPPING": "",
  "avg30_LIGHTNING_DEAL": "",
  "avg30_WAREHOUSE": "",
  "avg30_NEW_FBA": "",
  "avg30_COUNT_NEW": "",
  "avg30_COUNT_USED": "",
  "avg30_COUNT_REFURBISHED": "",
  "avg30_COUNT_COLLECTIBLE": "",
  "avg30_RATING": "",
  "avg30_COUNT_REVIEWS": "",
  "avg30_BUY_BOX_SHIPPING": "",
  "avg30_USED_NEW_SHIPPING": "",
  "avg30_USED_VERY_GOOD_SHIPPING": "",
  "avg30_USED_GOOD_SHIPPING": "",
  "avg30_USED_ACCEPTABLE_SHIPPING": "",
  "avg30_REFURBISHED_SHIPPING": "",
  "avg30_TRADE_IN": "",
  "avg365_AMAZON": "",
  "avg365_BUY_BOX_SHIPPING": "",
  "avg365_BUY_BOX_USED_SHIPPING": "",
  "avg365_COLLECTIBLE": "",
  "avg365_COUNT_COLLECTIBLE": "",
  "avg365_COUNT_NEW": "",
  "avg365_COUNT_REFURBISHED": "",
  "avg365_COUNT_REVIEWS": "",
  "avg365_COUNT_USED": "",
  "avg365_EBAY_NEW_SHIPPING": "",
  "avg365_EBAY_USED_SHIPPING": "",
  "avg365_LIGHTNING_DEAL": "",
  "avg365_LISTPRICE": "",
  "avg365_NEW": "",
  "avg365_NEW_FBA": "",
  "avg365_NEW_FBM_SHIPPING": "",
  "avg365_PRIME_EXCL": "",
  "avg365_RATING": "",
  "avg365_REFURBISHED": "",
  "avg365_REFURBISHED_SHIPPING": "",
  "avg365_RENT": "",
  "avg365_SALES": "",
  "avg365_TRADE_IN": "",
  "avg365_USED": "",
  "avg365_USED_ACCEPTABLE_SHIPPING": "",
  "avg365_USED_GOOD_SHIPPING": "",
  "avg365_USED_NEW_SHIPPING": "",
  "avg365_USED_VERY_GOOD_SHIPPING": "",
  "avg365_WAREHOUSE": "",
  "lastPriceChange_AMAZON": "",
  "lastPriceChange_NEW": "",
  "lastPriceChange_USED": "",
  "lastPriceChange_SALES": "",
  "lastPriceChange_LISTPRICE": "",
  "lastPriceChange_COLLECTIBLE": "",
  "lastPriceChange_REFURBISHED": "",
  "lastPriceChange_NEW_FBM_SHIPPING": "",
  "lastPriceChange_LIGHTNING_DEAL": "",
  "lastPriceChange_WAREHOUSE": "",
  "lastPriceChange_NEW_FBA": "",
  "lastPriceChange_COUNT_NEW": "",
  "lastPriceChange_COUNT_USED": "",
  "lastPriceChange_COUNT_REFURBISHED": "",
  "lastPriceChange_COUNT_COLLECTIBLE": "",
  "lastPriceChange_RATING": "",
  "lastPriceChange_COUNT_REVIEWS": "",
  "lastPriceChange_BUY_BOX_SHIPPING": "",
  "lastPriceChange_USED_NEW_SHIPPING": "",
  "lastPriceChange_USED_VERY_GOOD_SHIPPING": "",
  "lastPriceChange_USED_GOOD_SHIPPING": "",
  "lastPriceChange_USED_ACCEPTABLE_SHIPPING": "",
  "lastPriceChange_REFURBISHED_SHIPPING": "",
  "lastPriceChange_EBAY_NEW_SHIPPING": "",
  "lastPriceChange_EBAY_USED_SHIPPING": "",
  "lastPriceChange_TRADE_IN": "",
  "lastPriceChange_RENT": "",
  "lastPriceChange_BUY_BOX_USED_SHIPPING": "",
  "lastPriceChange_PRIME_EXCL": "",
  "backInStock_AMAZON": "",
  "backInStock_NEW": "",
  "backInStock_USED": "",
  "backInStock_SALES": "",
  "backInStock_LISTPRICE": "",
  "backInStock_COLLECTIBLE": "",
  "backInStock_REFURBISHED": "",
  "backInStock_NEW_FBM_SHIPPING": "",
  "backInStock_LIGHTNING_DEAL": "",
  "backInStock_WAREHOUSE": "",
  "backInStock_NEW_FBA": "",
  "backInStock_COUNT_NEW": "",
  "backInStock_COUNT_USED": "",
  "backInStock_COUNT_REFURBISHED": "",
  "backInStock_COUNT_COLLECTIBLE": "",
  "backInStock_RATING": "",
  "backInStock_COUNT_REVIEWS": "",
  "backInStock_BUY_BOX_SHIPPING": "",
  "backInStock_USED_NEW_SHIPPING": "",
  "backInStock_USED_VERY_GOOD_SHIPPING": "",
  "backInStock_USED_GOOD_SHIPPING": "",
  "backInStock_USED_ACCEPTABLE_SHIPPING": "",
  "backInStock_REFURBISHED_SHIPPING": "",
  "backInStock_TRADE_IN": "",
  "isLowest_AMAZON": "",
  "isLowest_BUY_BOX_SHIPPING": "",
  "isLowest_BUY_BOX_USED_SHIPPING": "",
  "isLowest_COLLECTIBLE": "",
  "isLowest_COUNT_COLLECTIBLE": "",
  "isLowest_COUNT_NEW": "",
  "isLowest_COUNT_REFURBISHED": "",
  "isLowest_COUNT_REVIEWS": "",
  "isLowest_COUNT_USED": "",
  "isLowest_EBAY_NEW_SHIPPING": "",
  "isLowest_EBAY_USED_SHIPPING": "",
  "isLowest_LIGHTNING_DEAL": "",
  "isLowest_LISTPRICE": "",
  "isLowest_NEW": "",
  "isLowest_NEW_FBA": "",
  "isLowest_NEW_FBM_SHIPPING": "",
  "isLowest_PRIME_EXCL": "",
  "isLowest_RATING": "",
  "isLowest_REFURBISHED": "",
  "isLowest_REFURBISHED_SHIPPING": "",
  "isLowest_RENT": "",
  "isLowest_SALES": "",
  "isLowest_TRADE_IN": "",
  "isLowest_USED": "",
  "isLowest_USED_ACCEPTABLE_SHIPPING": "",
  "isLowest_USED_GOOD_SHIPPING": "",
  "isLowest_USED_NEW_SHIPPING": "",
  "isLowest_USED_VERY_GOOD_SHIPPING": "",
  "isLowest_WAREHOUSE": "",
  "isLowest90_AMAZON": "",
  "isLowest90_BUY_BOX_SHIPPING": "",
  "isLowest90_BUY_BOX_USED_SHIPPING": "",
  "isLowest90_COLLECTIBLE": "",
  "isLowest90_COUNT_COLLECTIBLE": "",
  "isLowest90_COUNT_NEW": "",
  "isLowest90_COUNT_REFURBISHED": "",
  "isLowest90_COUNT_REVIEWS": "",
  "isLowest90_COUNT_USED": "",
  "isLowest90_EBAY_NEW_SHIPPING": "",
  "isLowest90_EBAY_USED_SHIPPING": "",
  "isLowest90_LIGHTNING_DEAL": "",
  "isLowest90_LISTPRICE": "",
  "isLowest90_NEW": "",
  "isLowest90_NEW_FBA": "",
  "isLowest90_NEW_FBM_SHIPPING": "",
  "isLowest90_PRIME_EXCL": "",
  "isLowest90_RATING": "",
  "isLowest90_REFURBISHED": "",
  "isLowest90_REFURBISHED_SHIPPING": "",
  "isLowest90_RENT": "",
  "isLowest90_SALES": "",
  "isLowest90_TRADE_IN": "",
  "isLowest90_USED": "",
  "isLowest90_USED_ACCEPTABLE_SHIPPING": "",
  "isLowest90_USED_GOOD_SHIPPING": "",
  "isLowest90_USED_NEW_SHIPPING": "",
  "isLowest90_USED_VERY_GOOD_SHIPPING": "",
  "isLowest90_WAREHOUSE": "",
};

// taken from the dropdown in keepa: (we should also allow custom string inputs)
const Colors = [
  "black",
  "white",
  "blue",
  "red",
  "multicolor",
  "silver",
  "green",
  "pink",
  "brown",
  "yellow",
  "one color",
  "purple",
  "gold",
  "multi",
  "gray",
  "navy",
  "orange",
  "beige",
  "clear",
  "navy blue",
  "a",
  "khaki",
  "style",
  "light blue",
];
